import { SeekLayout } from '@/components/seek/SeekLayout';
import { cn } from '@/lib/utils';
import { getCompletionColorClasses } from '@/lib/completionColors';
import { formatCandidateId } from '@/lib/candidateId';
import { getCurrencySymbol } from '@/lib/currency';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { detectTextDirection } from '@/lib/textDirection';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  User, FileText, Search, ArrowRight, MapPin, DollarSign, Clock,
  ChevronRight, Sparkles, Briefcase, Camera, Send, Check, X,
  GraduationCap, Languages, Award, CalendarDays, Building2,
  Video, Gift, CalendarCheck, Play, ExternalLink, Eye, ShieldOff,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCandidateProfile } from '@/hooks/useCandidateProfile';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { useSeekApplications } from '@/hooks/useSeekApplications';
import { useRecommendedJobs } from '@/hooks/useRecommendedJobs';
import { useSeekInvites } from '@/hooks/useSeekInvites';
import { useSeekInterviewSessions } from '@/hooks/useSeekInterviewSessions';
import { useSeekOfferSessions } from '@/hooks/useSeekOfferSessions';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AvatarEditorDialog } from '@/components/seek/AvatarEditorDialog';
import { differenceInDays } from 'date-fns';
import { formatLocalizedDate, formatRelativeTime } from '@/lib/localizedDate';
import { buildRecruitmentInterviewUrl } from '@/lib/candidateInterviewLinks';
import { useQuery } from '@tanstack/react-query';
import { useSeekServiceGovernance } from '@/hooks/useSeekServiceGovernance';
import { hasComputedSeekMatchScore, type SeekMatchScoreState } from '@/lib/seekMatchScoreState';
import { RESPONSIVE_ACTION_ROW, RESPONSIVE_BUTTON_TEXT, RESPONSIVE_HEADER_ROW, SAFE_TEXT_WRAP } from '@/lib/responsiveText';
import type { Tables } from '@/integrations/supabase/types';

type SeekJobView = Tables<'seek_job_catalog'>;
type SeekApplicationView = Tables<'seek_applications'> & {
  seek_job_catalog: SeekJobView | null;
  match_score: number | null;
  match_score_state: SeekMatchScoreState;
};
type RecommendedJob = SeekJobView & {
  match_score: number | null;
  match_score_state: SeekMatchScoreState;
};

function objectRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

const statusStyleKeys: Record<string, { labelKey: string; className: string }> = {
  applied: { labelKey: 'seek.status.applied', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  under_review: { labelKey: 'seek.status.under_review', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  shortlisted: { labelKey: 'seek.status.shortlisted', className: 'bg-seek/10 text-seek dark:bg-seek/20 dark:text-seek' },
  interview_invitation: { labelKey: 'seek.status.interview_invitation', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  interviewing: { labelKey: 'seek.status.interviewing', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  rejected: { labelKey: 'seek.status.rejected', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  hired: { labelKey: 'seek.status.hired', className: 'bg-seek/15 text-seek dark:bg-seek/25 dark:text-seek' },
};

export default function SeekDashboard() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const { profile: authProfile, user } = useSeekAuth();
  const navigate = useNavigate();
  const { profile, experiences, education, skills, languages, updateProfile } = useCandidateProfile();
  const { completionScore: completionPct } = useProfileCompletion();
  const { applications: applicationResults } = useSeekApplications();
  const applications: SeekApplicationView[] = applicationResults;
  const { recommendations: recommendationResults } = useRecommendedJobs(5);
  const recommendations: RecommendedJob[] = recommendationResults;
  const { invites, pendingCount, respond, isResponding } = useSeekInvites();
  const { toast } = useToast();
  const { sessions: interviewSessionsList } = useSeekInterviewSessions();
  const { offers: offerSessionsList } = useSeekOfferSessions();
  const cvBuilderGov = useSeekServiceGovernance('cv_builder');
  const jobDiscoveryGov = useSeekServiceGovernance('job_discovery');
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [previewInvite, setPreviewInvite] = useState<import('@/hooks/useSeekInvites').CandidateInvite | null>(null);
  const [secondInterviewDetail, setSecondInterviewDetail] = useState<{
    jobTitle: string; orgName: string | null; seekAppId: string; sessionToken: string; expiresAt: string | null;
  } | null>(null);
  const [f2fDeclineOpen, setF2fDeclineOpen] = useState(false);
  const [f2fDeclineReason, setF2fDeclineReason] = useState('');
  const [f2fDeclineNote, setF2fDeclineNote] = useState('');
  const [f2fDeclining, setF2fDeclining] = useState(false);
  const previewOrgId = previewInvite?.catalog_job?.hire_org_id;

  const { data: previewOrgInfo } = useQuery({
    queryKey: ['seek-invite-org-info', previewOrgId],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .rpc('get_public_org_profile', { _org_id: previewOrgId! });
      if (error || !data || data.length === 0) return null;
      return data[0];
    },
    enabled: !!previewOrgId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch 2nd interview notification details when dialog is open
  const secondInterviewSeekAppId = secondInterviewDetail?.seekAppId;
  const { data: secondInterviewNotif } = useQuery({
    queryKey: ['seek-2nd-interview-notif', secondInterviewSeekAppId],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('candidate_notifications')
        .select('title, body, payload_json, created_at')
        .eq('candidate_id', user!.id)
        .eq('notification_type', 'stage_second_interview')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error || !data) return null;
      // Find the one matching this seek_app_id
      const match = data.find((notification) => objectRecord(notification.payload_json)?.seek_application_id === secondInterviewSeekAppId);
      return match ?? data[0] ?? null;
    },
    enabled: !!secondInterviewSeekAppId && !!user?.id,
    staleTime: 60 * 1000,
  });

  const handleF2fDecline = async () => {
    if (!secondInterviewDetail?.seekAppId || !f2fDeclineReason) return;
    setF2fDeclining(true);
    try {
      // Get the hire_application_id from seek_applications
      const { data: seekApp } = await seekSupabase
        .from('seek_applications')
        .select('hire_application_id')
        .eq('id', secondInterviewDetail.seekAppId)
        .single();
      if (!seekApp?.hire_application_id) throw new Error('Application not found');

      // Call the f2f-response edge function
      await seekSupabase.functions.invoke('f2f-response', {
        body: {
          action: 'candidate_decline',
          application_id: seekApp.hire_application_id,
          reason: f2fDeclineReason,
          note: f2fDeclineNote.trim() || null,
        },
      });

      toast({ title: t('seek.dashboard.f2fDeclined'), description: t('seek.dashboard.f2fDeclinedDesc') });
      setF2fDeclineOpen(false);
      setSecondInterviewDetail(null);
      setF2fDeclineReason('');
      setF2fDeclineNote('');
    } catch (error: unknown) {
      console.error('F2F decline error:', error);
      toast({ title: t('seek.dashboard.f2fDeclineError'), variant: 'destructive' });
    } finally {
      setF2fDeclining(false);
    }
  };

  const { data: interviewSessions } = useQuery({
    queryKey: ['seek-interview-session-map', user?.id],
    queryFn: async () => {
      const { data, error } = await seekSupabase.rpc('get_candidate_interview_sessions', {
        p_candidate_id: user!.id,
      });
      if (error) return [];
      return (data ?? []) as { hire_application_id: string; session_token: string; session_status: string; expires_at: string | null }[];
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000,
  });

  // Map hire_application_id -> session info
  const interviewSessionMap = new Map(
    (interviewSessions ?? []).map((s) => [s.hire_application_id, s])
  );

  const handleAvatarSave = async (blob: Blob) => {
    if (!user) return;
    const path = `${user.id}/avatar.png`;
    const { error: uploadError } = await seekSupabase.storage
      .from('candidate-avatars')
      .upload(path, blob, { upsert: true, contentType: 'image/png' });
    if (uploadError) throw uploadError;
    const { data: urlData } = seekSupabase.storage
      .from('candidate-avatars')
      .getPublicUrl(path);
    const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    await updateProfile.mutateAsync({ avatar_url: avatarUrl });
    toast({ title: t('seek.dashboard.photoUpdated') });
  };

  // completionPct now comes from useProfileCompletion (single source of truth)

  const activeApps = applications.filter(
    (app) => {
      const jobStatus = app.seek_job_catalog?.status;
      const appStatus = app.application_status_candidate_view ?? 'applied';
      return jobStatus !== 'closed' && jobStatus !== 'expired' && appStatus !== 'withdrawn' && appStatus !== 'rejected';
    }
  );
  const recentApps = activeApps.slice(0, 5);

  const quickActions = [
    { title: t('seek.dashboard.reviewProfile'), subtitle: t('seek.dashboard.boostVisibility'), icon: User, path: '/seek/profile', blocked: false },
    { title: t('seek.dashboard.seekExpert'), subtitle: cvBuilderGov.isGlobalOff ? t('seek.dashboard.serviceUnavailable') : t('seek.dashboard.aiAnalysis'), icon: cvBuilderGov.isGlobalOff ? ShieldOff : FileText, path: '/seek/cv', blocked: !cvBuilderGov.allowed },
    { title: t('seek.dashboard.findJobsAction'), subtitle: jobDiscoveryGov.isGlobalOff ? t('seek.dashboard.serviceUnavailable') : t('seek.dashboard.discoverOpportunities'), icon: jobDiscoveryGov.isGlobalOff ? ShieldOff : Search, path: '/seek/jobs', blocked: !jobDiscoveryGov.allowed },
  ];

  const matchScoreLabel = (state: SeekMatchScoreState, score: number | null) => {
    if (state === 'computed' && typeof score === 'number') {
      return t('seek.dashboard.match', { score });
    }
    return t(`seek.jobs.matchScoreState.${state}`);
  };

  const formatDaysAgo = (dateStr: string) => {
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (days === 0) return t('seek.dashboard.today');
    if (days === 1) return t('seek.dashboard.dayAgo');
    return t('seek.dashboard.daysAgo', { count: days });
  };

  const formatPostedTime = (dateStr: string) => {
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (days === 0) return t('seek.dashboard.postedToday');
    if (days === 1) return t('seek.dashboard.postedDayAgo');
    if (days < 7) return t('seek.dashboard.postedDaysAgo', { count: days });
    return t('seek.dashboard.postedWeeksAgo', { count: Math.floor(days / 7) });
  };

  return (
    <SeekLayout>
      <div className="seek-page-container seek-page-stack">
        {/* Header */}
        <div className={`${RESPONSIVE_HEADER_ROW} seek-hero-panel p-5 sm:p-6 lg:p-7 gap-5`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0">
            <div className="relative group shrink-0">
              <Avatar className="w-20 h-20 border-2 border-seek/30 shadow-lg shadow-seek/10">
                <AvatarImage src={profile?.avatar_url ?? undefined} alt={t('seek.dashboard.profilePhoto')} className="object-cover" />
                <AvatarFallback className="bg-seek/10 text-seek text-2xl font-bold">
                  {authProfile?.full_name?.charAt(0)?.toUpperCase() ?? 'U'}
                </AvatarFallback>
              </Avatar>
              <button
                type="button"
                onClick={() => setAvatarDialogOpen(true)}
                className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              >
                <Camera className="w-5 h-5 text-white" />
              </button>
              <AvatarEditorDialog
                open={avatarDialogOpen}
                onOpenChange={setAvatarDialogOpen}
                onSave={handleAvatarSave}
                currentAvatarUrl={profile?.avatar_url}
              />
            </div>
            <div className="min-w-0 max-w-3xl">
              <span className="seek-section-kicker mb-3">
                <Award className="h-3.5 w-3.5" />
                {t('seek.sidebar.profileStrength')}
              </span>
              <h1 className="seek-page-title font-bold text-foreground font-display break-words">
                {t('seek.dashboard.welcome', { name: authProfile?.full_name?.split(' ')[0] || '' })}
              </h1>
              {profile?.candidate_number && (
                <span className="inline-flex items-center text-xs font-mono font-bold px-2 py-0.5 rounded bg-seek/10 text-seek border border-seek/20 mt-1">
                  {formatCandidateId(null, profile.candidate_number)}
                </span>
              )}
              {profile?.headline && (
                <p className={`text-sm text-foreground/70 mt-0.5 font-bold ${SAFE_TEXT_WRAP}`} dir={detectTextDirection(profile.headline)}>{profile.headline}</p>
              )}
              <p className={`text-sm text-muted-foreground mt-0.5 ${SAFE_TEXT_WRAP}`}>{t('seek.dashboard.guidingText')}</p>
            </div>
          </div>
          <div className={RESPONSIVE_ACTION_ROW}>
            <Button variant="outline" className={`rounded-lg hover:bg-seek/10 hover:text-seek hover:border-seek/20 dark:hover:bg-seek/20 dark:hover:text-seek dark:hover:border-seek/30 ${RESPONSIVE_BUTTON_TEXT}`} onClick={() => navigate('/seek/profile')}>
              {t('seek.dashboard.viewProfile')}
            </Button>
            <Button className={`rounded-lg bg-seek hover:bg-seek/90 text-seek-foreground ${RESPONSIVE_BUTTON_TEXT}`} onClick={() => navigate('/seek/jobs')}>
              {t('seek.dashboard.browseJobs')}
            </Button>
          </div>
        </div>

        {/* Profile Completion Banner */}
        {completionPct < 100 && (
          <Card className="seek-panel border-t-4 border-t-seek overflow-hidden relative">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-seek/10 flex items-center justify-center shrink-0 mt-0.5">
                <Clock className="w-5 h-5 text-seek" />
              </div>
              <div className="flex-1 min-w-0 z-10">
                <h3 className="font-semibold text-foreground text-lg font-display">{t('seek.dashboard.completeProfileBanner')}</h3>
                <p className="text-sm text-muted-foreground mt-0.5 mb-3">
                  {t('seek.dashboard.profileCompletionText', { pct: completionPct })}
                </p>
                <Progress value={completionPct} className={cn('h-2 mb-3', getCompletionColorClasses(completionPct).bar)} />
                <Button
                  className="rounded-lg bg-seek hover:bg-seek/90 text-seek-foreground gap-1.5"
                  size="sm"
                  onClick={() => navigate('/seek/profile')}
                >
                  {t('seek.dashboard.completeProfile')} <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
              <div className="absolute end-8 top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.06]">
                <Clock className="w-36 h-36 text-seek" strokeWidth={1} />
              </div>
            </CardContent>
          </Card>
        )}


        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {quickActions.map((action) => (
            <Card
              key={action.path}
              className={`group seek-card-interactive seek-human-card border-t-4 ${
                action.blocked
                  ? 'border-t-muted-foreground/30 opacity-60 cursor-not-allowed'
                  : 'border-t-seek cursor-pointer'
              }`}
              onClick={() => !action.blocked && navigate(action.path)}
            >
              <CardContent className="p-5 flex items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                  action.blocked ? 'bg-muted' : 'bg-seek/10'
                }`}>
                  <action.icon className={`w-5 h-5 ${action.blocked ? 'text-muted-foreground' : 'text-seek'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground text-sm">{action.title}</p>
                  <p className="text-xs text-muted-foreground">{action.subtitle}</p>
                </div>
                <ChevronRight className={`w-4 h-4 shrink-0 ${
                  action.blocked ? 'text-muted-foreground/30' : 'text-muted-foreground/50 group-hover:text-seek transition-colors'
                }`} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom Two-Column Section */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] xl:grid-cols-[1fr_380px] 3xl:grid-cols-[1fr_420px] gap-5 sm:gap-6">
          {/* Your Applications */}
          <Card className="seek-panel seek-status-rail">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="text-lg font-display">{t('seek.dashboard.yourApplications')}</CardTitle>
                <p className="text-sm text-muted-foreground">{t('seek.dashboard.trackStatus')}</p>
              </div>
              {activeApps.length > 5 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground gap-1"
                  onClick={() => navigate('/seek/applications')}
                >
                  {t('common.viewAll')} <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="pt-0">
              {recentApps.length === 0 ? (
                <div className="seek-empty-state text-center py-8 px-4">
                  <Search className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3 max-w-sm mx-auto">{t('seek.dashboard.seekOpportunities')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => navigate('/seek/jobs')}
                  >
                    {t('seek.dashboard.browseJobs')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentApps.map((app) => {
                    const job = app.seek_job_catalog;
                    const status = app.application_status_candidate_view ?? 'applied';
                    const style = statusStyleKeys[status] ?? statusStyleKeys.applied;
                     const matchScore = app.match_score;
                     const matchScoreState = app.match_score_state ?? (matchScore != null ? 'computed' : 'missing');
                     const hasMatchScore = hasComputedSeekMatchScore({ state: matchScoreState, score: matchScore });
                     const jobLocation = job?.location_text
                       || [job?.city, job?.country].filter(Boolean).join(', ');
                    return (
                      <div key={app.id} className="seek-card-interactive p-4 cursor-pointer" onClick={() => navigate(`/seek/applications/${app.id}`)}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-foreground" dir={detectTextDirection(job?.title)}>{job?.title ?? t('seek.dashboard.unknownJob')}</h4>
                            <Badge variant="secondary" className={`text-[10px] uppercase tracking-wider font-bold ${style.className}`}>
                              {t(style.labelKey)}
                            </Badge>
                          </div>
                          <span className={`text-sm font-bold rounded-lg px-2.5 py-0.5 whitespace-nowrap border ${
                            hasMatchScore && (matchScore ?? -1) >= 80 ? 'text-seek bg-seek/10 border-seek/20 dark:text-seek dark:bg-seek/20 dark:border-seek/30' :
                            hasMatchScore && (matchScore ?? -1) >= 60 ? 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-900/30 dark:border-amber-700' :
                            hasMatchScore ? 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-700' :
                            'text-muted-foreground bg-muted/50 border-border'
                          }`}>
                            {matchScoreLabel(matchScoreState, matchScore)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2 text-end" dir={detectTextDirection(job?.org_name)}>{job?.org_name}</p>
                        <div className="flex flex-wrap items-center gap-2">
                           {jobLocation && (
                             <Badge variant="secondary" className="bg-muted text-muted-foreground font-normal gap-1 rounded-full px-3 py-1">
                               <MapPin className="w-3 h-3" /> {jobLocation}
                            </Badge>
                          )}
                          {job?.employment_type && (
                            <Badge variant="secondary" className="bg-muted text-muted-foreground font-normal gap-1 rounded-full px-3 py-1 capitalize">
                              <Briefcase className="w-3 h-3" /> {job.employment_type.replace(/_/g, ' ').replace(/-/g, ' ')}
                            </Badge>
                          )}
                          {job?.work_mode && (
                            <Badge variant="secondary" className="bg-muted text-muted-foreground font-normal gap-1 rounded-full px-3 py-1 capitalize">
                              {job.work_mode.replace(/_/g, ' ')}
                            </Badge>
                          )}
                          {(job?.salary_min > 0 || job?.salary_max > 0) && (
                            <Badge variant="secondary" className="bg-muted text-muted-foreground font-normal gap-1 rounded-full px-3 py-1">
                              <DollarSign className="w-3 h-3" />
                              {job.salary_min > 0 ? `${job.salary_currency ?? '$'}${Math.round(job.salary_min / 1000)}k` : ''}
                              {job.salary_min > 0 && job.salary_max > 0 ? ' - ' : ''}
                              {job.salary_max > 0 ? `${job.salary_currency ?? '$'}${Math.round(job.salary_max / 1000)}k` : ''}
                            </Badge>
                          )}
                          <Badge variant="secondary" className="bg-muted text-muted-foreground font-normal gap-1 rounded-full px-3 py-1">
                            <Clock className="w-3 h-3" />
                            {formatDaysAgo(app.created_at)}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right sidebar */}
          <div className="space-y-6">
            {/* Unified Invitations & Activity */}
            {(() => {
              // Build unified activity items from multiple sources
              const pendingInviteItems = invites.filter(i => {
                if (i.invite_status !== 'pending' && i.invite_status !== 're-invited') return false;
                // Exclude expired invites
                if (i.expires_at && new Date(i.expires_at) < new Date()) return false;
                return true;
              }).map(inv => {
                return {
                  type: 'employer_invite' as const,
                  id: `inv-${inv.id}`,
                  title: inv.job_title ?? t('seek.dashboard.employerInvite'),
                  subtitle: inv.org_name ?? '',
                  icon: Send,
                  iconColor: 'text-seek',
                  borderColor: 'border-seek/40 bg-seek/[0.02]',
                  date: inv.created_at,
                  invite: inv,
                  isExpired: false,
                  expiresAt: inv.expires_at,
                };
              });

              // Real interview session items (AI interviews)
              const now = new Date();
              const interviewItems = interviewSessionsList
                .filter(s => {
                  // Only show active/pending sessions
                  if (s.status !== 'invited' && s.status !== 'sent') return false;
                  // Exclude expired sessions
                  if (s.expires_at && new Date(s.expires_at) <= now) return false;
                  // Exclude F2F interviews that were declined or cancelled
                  if (s.session_number > 1 && s.f2f_response_status) return false;
                  return true;
                })
                .map(s => {
                  const isExpired = s.expires_at ? new Date(s.expires_at) <= now : false;
                  const is2nd = s.session_number > 1;
                  return {
                    type: 'interview_session' as const,
                    id: `sess-${s.session_id}`,
                    title: s.job_title,
                    subtitle: s.org_name ?? '',
                    icon: is2nd ? CalendarCheck : Video,
                    iconColor: is2nd ? 'text-indigo-600 dark:text-indigo-400' : 'text-purple-600 dark:text-purple-400',
                    borderColor: is2nd ? 'border-indigo-400/40 bg-indigo-500/[0.02]' : 'border-purple-400/40 bg-purple-500/[0.02]',
                    date: s.session_created_at,
                    label: is2nd ? t('seek.dashboard.f2fInterview') : t('seek.dashboard.newInterview'),
                    interviewToken: s.token,
                    expiresAt: s.expires_at,
                    isExpired,
                    is2ndInterview: is2nd,
                    seekAppId: s.seek_app_id,
                    actionUrl: is2nd ? undefined : buildRecruitmentInterviewUrl(s.token),
                    actionLabel: is2nd ? t('seek.dashboard.seeDetails') : t('seek.dashboard.startInterview'),
                    actionIcon: is2nd ? Eye : Play,
                  };
                });

              // Real offer items
              const offerItems = offerSessionsList
                .filter(o => {
                  if (o.status !== 'sent' && o.status !== 'viewed') return false;
                  if (o.expires_at && new Date(o.expires_at) <= now) return false;
                  return true;
                })
                .map(o => {
                  const isExpired = o.expires_at ? new Date(o.expires_at) <= now : false;
                  return {
                    type: 'offer_session' as const,
                    id: `offer-${o.offer_id}`,
                    title: o.job_title,
                    subtitle: o.org_name ?? '',
                    icon: Gift,
                    iconColor: 'text-seek',
                    borderColor: 'border-seek/40 bg-seek/[0.02]',
                    date: o.sent_at ?? o.created_at,
                    label: t('seek.dashboard.jobOffer'),
                    interviewToken: null,
                    expiresAt: o.expires_at,
                    isExpired,
                    actionUrl: `/offer/${o.token}`,
                    actionLabel: t('seek.dashboard.reviewOffer'),
                    actionIcon: ExternalLink,
                  };
                });

              const allItems = [...pendingInviteItems, ...interviewItems, ...offerItems]
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

              const totalCount = allItems.length;

              return (
                <Card className="seek-panel border-s-4 border-s-seek">
                  <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <div className="flex items-center gap-2">
                      <Send className="w-5 h-5 text-seek" />
                      <CardTitle className="text-lg font-display">{t('seek.dashboard.invitations')}</CardTitle>
                    </div>
                    {totalCount > 0 && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{totalCount}</Badge>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    {totalCount === 0 ? (
                      <div className="text-center py-6">
                        <Send className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground mb-1">{t('seek.dashboard.noPendingInvitations')}</p>
                        <p className="text-xs text-muted-foreground">{t('seek.dashboard.enableDiscoverability')}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {allItems.slice(0, 3).map((item) => {
                          if (item.type === 'employer_invite') {
                            const inv = item.invite!;
                            const isExpired = item.isExpired;
                            const invExpiresAt = item.expiresAt;
                            const invDaysLeft = invExpiresAt
                              ? differenceInDays(new Date(invExpiresAt), new Date())
                              : null;
                            return (
                              <div
                                key={item.id}
                                className={`seek-card-interactive p-3.5 ${isExpired ? 'opacity-60' : 'cursor-pointer'} ${item.borderColor}`}
                                onClick={() => !isExpired && setPreviewInvite(inv)}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <Send className={`w-3.5 h-3.5 shrink-0 ${item.iconColor}`} />
                                  <span className="font-semibold text-foreground text-sm flex-1 truncate" dir={detectTextDirection(inv.job_title)}>
                                    {inv.job_title ?? t('seek.dashboard.employerInvite')}
                                  </span>
                                  {isExpired ? (
                                    <Badge variant="destructive" className="text-[10px] shrink-0">
                                      {t('seek.dashboard.expired')}
                                    </Badge>
                                  ) : (
                                    <InviteStatusBadge status={inv.invite_status} t={t} />
                                  )}
                                </div>

                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mb-1.5">
                                  {inv.org_name && <span className="font-medium">{inv.org_name}</span>}
                                  {inv.catalog_job?.location_text && (
                                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {inv.catalog_job.location_text}</span>
                                  )}
                                  {inv.catalog_job?.employment_type && (
                                    <span className="capitalize">{inv.catalog_job.employment_type.replace(/_/g, ' ')}</span>
                                  )}
                                  {(inv.catalog_job?.salary_min || inv.catalog_job?.salary_max) && (
                                    <span className="flex items-center gap-0.5">
                                      <DollarSign className="w-3 h-3" />
                                      {inv.catalog_job.salary_currency ?? '$'}
                                      {inv.catalog_job.salary_min ? `${Math.round(inv.catalog_job.salary_min / 1000)}k` : ''}
                                      {inv.catalog_job.salary_min && inv.catalog_job.salary_max ? ' - ' : ''}
                                      {inv.catalog_job.salary_max ? `${inv.catalog_job.salary_currency ?? '$'}${Math.round(inv.catalog_job.salary_max / 1000)}k` : ''}
                                    </span>
                                  )}
                                </div>

                                {inv.invite_message && (
                                  <p className="text-xs text-muted-foreground mb-1.5 italic" dir={detectTextDirection(inv.invite_message)}>"{inv.invite_message}"</p>
                                )}
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-[11px] text-muted-foreground/60">
                                    {formatRelativeTime(inv.created_at, i18n.resolvedLanguage ?? i18n.language)}
                                  </p>
                                  {!isExpired && invDaysLeft !== null && (
                                    <span className={`text-[10px] font-medium ${invDaysLeft <= 1 ? 'text-destructive' : invDaysLeft <= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                                      {invDaysLeft <= 0
                                        ? t('seek.dashboard.expiresToday')
                                        : t('seek.dashboard.daysLeft', { count: invDaysLeft })}
                                    </span>
                                  )}
                                </div>

                                {!isExpired && (
                                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      size="sm"
                                      className="flex-1 bg-seek hover:bg-seek/90 text-seek-foreground rounded-lg text-xs h-8"
                                      onClick={() => respond({ inviteId: inv.id, status: 'accepted' })}
                                      disabled={isResponding}
                                    >
                                      <Check className="w-3.5 h-3.5 me-1" /> {t('seek.dashboard.accept')}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="flex-1 rounded-lg text-xs h-8 hover:bg-pink-500/10 hover:text-pink-500 hover:border-pink-500/30 active:bg-destructive active:text-white active:border-destructive"
                                      onClick={() => respond({ inviteId: inv.id, status: 'declined' })}
                                      disabled={isResponding}
                                    >
                                      <X className="w-3.5 h-3.5 me-1" /> {t('seek.dashboard.decline')}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // Session/offer activity item with action button
                          const ItemIcon = item.icon;
                          const ActionIcon = item.actionIcon;
                          const expiresAt = item.expiresAt;
                          const daysLeft = expiresAt
                            ? differenceInDays(new Date(expiresAt), new Date())
                            : null;
                          const isItemExpired = item.isExpired;
                          return (
                            <div
                              key={item.id}
                              className={`seek-card-interactive p-3.5 ${isItemExpired ? 'opacity-60' : ''} ${item.borderColor}`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <ItemIcon className={`w-3.5 h-3.5 shrink-0 ${isItemExpired ? 'text-muted-foreground' : item.iconColor}`} />
                                <span className="font-semibold text-foreground text-sm flex-1 truncate" dir={detectTextDirection(item.title)}>
                                  {item.title}
                                </span>
                                {isItemExpired ? (
                                  <Badge variant="destructive" className="text-[10px] shrink-0">
                                    {t('seek.dashboard.expired')}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px] shrink-0">
                                    {item.label}
                                  </Badge>
                                )}
                              </div>
                              {item.subtitle && (
                                <p className="text-xs text-muted-foreground font-medium mb-1" dir={detectTextDirection(item.subtitle)}>{item.subtitle}</p>
                              )}
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-[11px] text-muted-foreground/60">
                                  {formatRelativeTime(item.date, i18n.resolvedLanguage ?? i18n.language)}
                                </p>
                                {!isItemExpired && daysLeft !== null && (
                                  <span className={`text-[10px] font-medium ${daysLeft <= 2 ? 'text-destructive' : daysLeft <= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                                    {daysLeft <= 0
                                      ? t('seek.dashboard.expiresToday')
                                      : t('seek.dashboard.daysLeft', { count: daysLeft })}
                                  </span>
                                )}
                              </div>
                              {!isItemExpired && (
                                <Button
                                  size="sm"
                                  className="w-full bg-seek hover:bg-seek/90 text-seek-foreground rounded-lg text-xs h-8 gap-1.5"
                                  onClick={() => {
                                    if ('is2ndInterview' in item && item.is2ndInterview) {
                                      setSecondInterviewDetail({
                                        jobTitle: item.title,
                                        orgName: item.subtitle || null,
                                        seekAppId: item.seekAppId,
                                        sessionToken: item.interviewToken,
                                        expiresAt: item.expiresAt ?? null,
                                      });
                                    } else if (item.actionUrl) {
                                      window.open(item.actionUrl, '_blank');
                                    }
                                  }}
                                >
                                  <ActionIcon className="w-3.5 h-3.5" /> {item.actionLabel}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                        {totalCount > 3 && (
                          <Button
                            variant="ghost"
                            className="w-full text-seek hover:text-seek hover:bg-seek/10 text-sm"
                            onClick={() => navigate('/seek/notifications')}
                          >
                            {t('common.viewAll')} ({totalCount})
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Recommended for You */}
            <Card className="seek-panel border-s-4 border-s-seek">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-seek" />
                  <CardTitle className="text-lg font-display">{t('seek.dashboard.recommendedForYou')}</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground">{t('seek.dashboard.basedOnProfile')}</p>
              </CardHeader>
              <CardContent className="pt-0">
                {recommendations.length === 0 ? (
                  <div className="text-center py-6">
                    <Sparkles className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-1">{t('seek.dashboard.completeForRecommendations')}</p>
                    <p className="text-xs text-muted-foreground">{t('seek.dashboard.matchJobsText')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recommendations.filter((job) => {
                      const jobMatchScore = job.match_score;
                      const jobMatchState = job.match_score_state ?? (jobMatchScore != null ? 'computed' : 'missing');
                      return hasComputedSeekMatchScore({ state: jobMatchState, score: jobMatchScore }) && (jobMatchScore ?? -1) >= 80;
                    }).map((job) => {
                      const jobMatchScore = job.match_score;
                      const jobMatchState = job.match_score_state ?? (jobMatchScore != null ? 'computed' : 'missing');
                      const hasJobMatchScore = hasComputedSeekMatchScore({ state: jobMatchState, score: jobMatchScore });
                      return (
                      <div key={job.id} className="seek-card-interactive p-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-semibold text-foreground text-sm" dir={detectTextDirection(job.title)}>{job.title}</h4>
                          <span className={`text-sm font-bold whitespace-nowrap rounded-lg px-2.5 py-0.5 border ${
                            hasJobMatchScore && (jobMatchScore ?? -1) >= 80 ? 'text-seek bg-seek/10 border-seek/20 dark:text-seek dark:bg-seek/20 dark:border-seek/30' :
                            hasJobMatchScore && (jobMatchScore ?? -1) >= 60 ? 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-900/30 dark:border-amber-700' :
                            hasJobMatchScore ? 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-700' :
                            'text-muted-foreground bg-muted/50 border-border'
                          }`}>
                            {matchScoreLabel(jobMatchState, jobMatchScore)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2" dir={detectTextDirection(job.org_name)}>{job.org_name}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mb-3">
                          {job.location_text && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {job.location_text}
                            </span>
                          )}
                          {(job.salary_min || job.salary_max) && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {job.salary_currency ?? '$'}{job.salary_min ? `${Math.round(job.salary_min / 1000)}k` : ''}
                              {job.salary_max ? ` - ${job.salary_currency ?? '$'}${Math.round(job.salary_max / 1000)}k` : ''}
                            </span>
                          )}
                          {job.published_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {t('seek.dashboard.posted', { time: formatPostedTime(job.published_at) })}
                            </span>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full rounded-lg text-xs"
                          onClick={() => navigate(`/seek/jobs?highlight=${job.id}`)}
                        >
                          {t('seek.dashboard.viewDetails')}
                        </Button>
                      </div>
                    );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Invite Job Preview Dialog */}
      <Dialog open={!!previewInvite} onOpenChange={(open) => { if (!open) setPreviewInvite(null); }}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-display" dir={detectTextDirection(previewInvite?.job_title)}>{previewInvite?.job_title ?? previewInvite?.catalog_job?.title ?? t('seek.dashboard.jobInvitation')}</DialogTitle>
            <DialogDescription className="flex items-center gap-2 text-sm">
              <Building2 className="w-3.5 h-3.5" />
              {previewInvite?.org_name ?? previewInvite?.catalog_job?.org_name ?? ''}
              {previewInvite?.catalog_job?.department && (
                <span className="text-muted-foreground">• {previewInvite.catalog_job.department}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {previewInvite && (() => {
            const job = previewInvite.catalog_job;
            const toStringList = (value: unknown): string[] => {
              if (Array.isArray(value)) {
                return value
                  .flatMap((value) => {
                    if (typeof value === 'string') return [value];
                    const record = objectRecord(value);
                    if (!record) return [];
                    return [String(record.name ?? record.text ?? JSON.stringify(record))];
                  })
                  .map((s) => s.trim())
                  .filter(Boolean);
              }
              if (value && typeof value === 'object') {
                return Object.values(value as Record<string, unknown>)
                  .flatMap((v) => (Array.isArray(v) ? v : [v]))
                  .map((v) => (typeof v === 'string' ? v : String(v ?? '')))
                  .map((s) => s.trim())
                  .filter(Boolean);
              }
              return [];
            };

            const rawRequirements = toStringList(job?.requirements_json);
            const requirements = rawRequirements
              .flatMap((item) => item.split(/\n|(?:^|\s)[•\-–]\s/).map((s) => s.replace(/^\s*[•\-–]\s*/, '').trim()))
              .filter(Boolean)
              .filter((item) => !(/^[\p{L}\s-]+:\s*$/u.test(item) && item.length < 40));

            const jobSkills = toStringList(job?.skills_json);
            const certifications = toStringList(job?.certifications_json);
            const jobLanguages = toStringList(job?.languages_json);
            const locationLine = job?.location_text ?? [job?.city, job?.country].filter(Boolean).join(', ');
            const showSalary = job?.salary_visibility_mode !== 'hidden' && ((job?.salary_min ?? 0) > 0 || (job?.salary_max ?? 0) > 0);

            const renderDescriptionSections = () => {
              const raw = job?.description ?? '';
              const lines = raw.split('\n');
              const sections: { title: string; content: string[] }[] = [];
              let current: { title: string; content: string[] } = { title: t('seek.dashboard.aboutThisRole'), content: [] };

              lines.forEach((line) => {
                const trimmed = line.trim();
                if (/^[\p{L}][\p{L}\s/&()-]+:\s*$/u.test(trimmed) && trimmed.length < 60) {
                  if (current.content.some((l) => l.trim())) sections.push(current);
                  current = { title: trimmed.replace(/:\s*$/, ''), content: [] };
                } else {
                  current.content.push(line);
                }
              });

              if (current.content.some((l) => l.trim())) sections.push(current);
              if (sections.length === 0 && raw.trim()) sections.push({ title: t('seek.dashboard.aboutThisRole'), content: [raw] });
              if (!sections.length) return null;

              const renderBody = (contentLines: string[]) => {
                const text = contentLines.join('\n').trim();
                const allLines = text.split('\n').filter((l) => l.trim());
                const bulletLines = allLines.filter((l) => /^\s*[•\-–]/.test(l));
                const plainLines = allLines.filter((l) => !/^\s*[•\-–]/.test(l));

                if (bulletLines.length > 0) {
                  return (
                    <>
                      {plainLines.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {plainLines.map((p, i) => <p key={i} className="text-sm text-muted-foreground leading-relaxed" dir={detectTextDirection(p)}>{p.trim()}</p>)}
                        </div>
                      )}
                      <ul className="space-y-3">
                        {bulletLines.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground" dir={detectTextDirection(item)}>
                             <Check className="w-4 h-4 text-seek shrink-0 mt-0.5" />
                             <span>{item.replace(/^\s*[•\-–]\s*/, '').trim()}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  );
                }

                return (
                  <div className="space-y-3">
                    {allLines.map((p, i) => <p key={i} className="text-sm text-muted-foreground leading-relaxed" dir={detectTextDirection(p)}>{p.trim()}</p>)}
                  </div>
                );
              };

              return (
                <div className="space-y-4">
                  {sections.map((section, idx) => (
                    <Card key={idx} className="seek-panel p-5">
                      <h4 className="font-display font-semibold text-foreground text-base mb-3" dir={detectTextDirection(section.title)}>{section.title}</h4>
                      {renderBody(section.content)}
                    </Card>
                  ))}
                </div>
              );
            };

            return (
              <div className="space-y-5">
                {/* Meta pills */}
                <div className="flex flex-wrap gap-2">
                  {locationLine && (
                    <Badge variant="outline" className="text-xs"><MapPin className="w-3 h-3 me-1" />{locationLine}</Badge>
                  )}
                  {job?.employment_type && (
                    <Badge variant="outline" className="text-xs capitalize"><Briefcase className="w-3 h-3 me-1" />{job.employment_type.replace(/_/g, ' ')}</Badge>
                  )}
                  {job?.work_mode && (
                    <Badge variant="outline" className="text-xs capitalize">{job.work_mode.replace(/_/g, ' ')}</Badge>
                  )}
                  {job?.seniority && (
                    <Badge variant="outline" className="text-xs capitalize">{job.seniority.replace(/_/g, ' ')}</Badge>
                  )}
                  {job?.min_experience_years != null && (
                    <Badge variant="outline" className="text-xs">{t('seek.dashboard.yrsExperience', { count: job.min_experience_years })}</Badge>
                  )}
                  {job?.published_at && (
                    <Badge variant="outline" className="text-xs">{t('seek.dashboard.published', { date: formatLocalizedDate(job.published_at, 'PP', language) })}</Badge>
                  )}
                  {job?.closes_at && (
                    <Badge variant="outline" className="text-xs"><CalendarDays className="w-3 h-3 me-1" />{t('seek.dashboard.closes', { date: formatLocalizedDate(job.closes_at, 'PP', language) })}</Badge>
                  )}
                </div>

                {/* Key details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {showSalary && (
                    <div className="rounded-lg bg-muted/40 p-3">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium mb-0.5">{t('seek.dashboard.salary')}</p>
                      <p className="text-sm font-semibold">
                        {getCurrencySymbol(job?.salary_currency ?? 'SAR')}{' '}
                        {job?.salary_min && job.salary_min > 0 ? job.salary_min.toLocaleString() : ''}
                        {job?.salary_max && job.salary_max > 0 ? `${job?.salary_min && job.salary_min > 0 ? ' – ' : ''}${job.salary_max.toLocaleString()}` : (job?.salary_min && job.salary_min > 0 ? '+' : '')}
                      </p>
                    </div>
                  )}
                  <div className="rounded-lg bg-muted/40 p-3">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium mb-0.5">{t('seek.dashboard.experience')}</p>
                    <p className="text-sm font-semibold">{job?.min_experience_years != null ? t('seek.dashboard.yearsPlus', { count: job.min_experience_years }) : t('seek.dashboard.notSpecified')}</p>
                  </div>
                </div>

                {/* Invite message */}
                {previewInvite.invite_message && (
                  <div className="bg-seek/5 border border-seek/20 rounded-lg p-3">
                    <p className="text-xs font-medium text-seek mb-1">{t('seek.dashboard.messageFromEmployer')}</p>
                    <p className="text-sm italic" dir={detectTextDirection(previewInvite.invite_message)}>"{previewInvite.invite_message}"</p>
                  </div>
                )}

                {/* Description sections */}
                {job?.description && (
                  <div>
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><FileText className="w-4 h-4 text-muted-foreground" /> {t('seek.dashboard.jobDetails')}</h4>
                    {renderDescriptionSections()}
                  </div>
                )}

                {/* Requirements + Skills etc */}
                {(() => {
                  const isArabicActionLine = (line: string) =>
                    /^\s*(?:إدارة|إعداد|تطوير|مراقبة|العمل|تقديم|تقييم|قيادة|ضمان|تنسيق|متابعة|تحليل|وضع|تنفيذ)\s+/.test(line);
                  const isEnglishActionLine = (line: string) =>
                    /^\s*(?:Responsible for|Manage|Lead|Develop|Prepare|Monitor|Coordinate|Oversee|Evaluate|Work with|Provide|Ensure)\b/i.test(line);

                  const { requirementsOnly, responsibilities } = requirements.reduce(
                    (acc, item) => {
                      if (isArabicActionLine(item) || isEnglishActionLine(item)) {
                        acc.responsibilities.push(item);
                      } else {
                        acc.requirementsOnly.push(item);
                      }
                      return acc;
                    },
                    { requirementsOnly: [] as string[], responsibilities: [] as string[] },
                  );

                  if (
                    requirementsOnly.length === 0 &&
                    responsibilities.length === 0 &&
                    jobSkills.length === 0 &&
                    certifications.length === 0 &&
                    jobLanguages.length === 0
                  ) return null;

                  return (
                    <Card className="seek-panel p-5 space-y-5">
                      {requirementsOnly.length > 0 && (
                        <div>
                          <h4 className="font-display font-semibold text-foreground text-base mb-3">{t('seek.dashboard.requirements')}</h4>
                          <ul className="space-y-2.5">
                            {requirementsOnly.map((req, i) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2.5" dir={detectTextDirection(req)}>
                                 <Check className="w-4 h-4 text-seek mt-0.5 shrink-0" />
                                 <span>{req}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {responsibilities.length > 0 && (
                        <div>
                          <h4 className="font-display font-semibold text-foreground text-base mb-3">{t('seek.dashboard.keyResponsibilities')}</h4>
                          <ul className="space-y-2.5">
                            {responsibilities.map((item, i) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2.5" dir={detectTextDirection(item)}>
                                 <Check className="w-4 h-4 text-seek mt-0.5 shrink-0" />
                                 <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {jobSkills.length > 0 && (
                        <div>
                          <h4 className="font-display font-semibold text-foreground text-base mb-3 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-muted-foreground" /> {t('seek.dashboard.requiredSkills')}</h4>
                          <div className="flex flex-wrap gap-2">
                            {jobSkills.map((s, i) => (
                              <span key={i} className="inline-block px-3 py-1.5 rounded-lg border border-seek/20 bg-seek/10 text-seek text-xs font-medium" dir={detectTextDirection(s)}>{s}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {certifications.length > 0 && (
                        <div>
                          <h4 className="font-display font-semibold text-foreground text-base mb-3 flex items-center gap-1.5"><Award className="w-4 h-4 text-muted-foreground" /> {t('seek.dashboard.certifications')}</h4>
                          <div className="flex flex-wrap gap-2">
                            {certifications.map((c, i) => (
                              <span key={i} className="inline-block px-3 py-1.5 rounded-lg border border-seek/20 bg-seek/10 text-seek text-xs font-medium" dir={detectTextDirection(c)}>{c}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {jobLanguages.length > 0 && (
                        <div>
                          <h4 className="font-display font-semibold text-foreground text-base mb-3 flex items-center gap-1.5"><Languages className="w-4 h-4 text-muted-foreground" /> {t('seek.dashboard.languages')}</h4>
                          <div className="flex flex-wrap gap-2">
                            {jobLanguages.map((l, i) => (
                              <span key={i} className="inline-block px-3 py-1.5 rounded-lg border border-seek/20 bg-seek/10 text-seek text-xs font-medium" dir={detectTextDirection(l)}>{l}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })()}

                {/* About company */}
                {previewOrgInfo && (previewOrgInfo.company_description || previewOrgInfo.company_mission || previewOrgInfo.company_benefits) && (
                  <Card className="seek-panel p-5 space-y-4">
                    <h4 className="font-display font-semibold text-foreground text-base flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-seek" />
                      {t('seek.dashboard.aboutCompany', { name: previewInvite?.org_name ?? job?.org_name ?? '' })}
                    </h4>
                    {previewOrgInfo.company_description && (
                      <p className="text-sm text-muted-foreground leading-relaxed" dir={detectTextDirection(previewOrgInfo.company_description)}>{previewOrgInfo.company_description}</p>
                    )}
                    {previewOrgInfo.company_mission && (
                      <div>
                        <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1">{t('seek.dashboard.missionVision')}</p>
                        <p className="text-sm text-muted-foreground leading-relaxed" dir={detectTextDirection(previewOrgInfo.company_mission)}>{previewOrgInfo.company_mission}</p>
                      </div>
                    )}
                    {previewOrgInfo.company_benefits && (
                      <div>
                        <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1">{t('seek.dashboard.benefitsPerks')}</p>
                        <p className="text-sm text-muted-foreground leading-relaxed" dir={detectTextDirection(previewOrgInfo.company_benefits)}>{previewOrgInfo.company_benefits}</p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {previewOrgInfo.industry && <span className="bg-muted/40 px-2.5 py-1 rounded-md">{previewOrgInfo.industry}</span>}
                      {previewOrgInfo.company_size && <span className="bg-muted/40 px-2.5 py-1 rounded-md">{previewOrgInfo.company_size} {t('seek.dashboard.employeesLabel')}</span>}
                      {previewOrgInfo.founded_year && <span className="bg-muted/40 px-2.5 py-1 rounded-md">{t('seek.dashboard.established', { year: previewOrgInfo.founded_year })}</span>}
                      {previewOrgInfo.headquarters && <span className="bg-muted/40 px-2.5 py-1 rounded-md">{previewOrgInfo.headquarters}</span>}
                    </div>
                  </Card>
                )}

                {/* Actions */}
                {previewInvite.invite_status === 'pending' && (
                  <div className="flex gap-2 pt-3 border-t">
                    <Button
                      className="flex-1 bg-seek hover:bg-seek/90 text-seek-foreground"
                      onClick={() => { respond({ inviteId: previewInvite.id, status: 'accepted' }); setPreviewInvite(null); }}
                      disabled={isResponding}
                    >
                      <Check className="w-4 h-4 me-1.5" /> {t('seek.dashboard.acceptInvitation')}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => { respond({ inviteId: previewInvite.id, status: 'declined' }); setPreviewInvite(null); }}
                      disabled={isResponding}
                    >
                      <X className="w-4 h-4 me-1.5" /> {t('seek.dashboard.decline')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* 2nd Interview Details Dialog */}
      <Dialog open={!!secondInterviewDetail} onOpenChange={(open) => { if (!open) setSecondInterviewDetail(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-display flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              {t('seek.dashboard.secondInterviewDetails')}
            </DialogTitle>
            <DialogDescription>
              {secondInterviewDetail?.jobTitle}
              {secondInterviewDetail?.orgName && ` — ${secondInterviewDetail.orgName}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {(() => {
              const payload = objectRecord(secondInterviewNotif?.payload_json);
              const scheduledDate = optionalString(payload?.scheduled_date);
              const scheduledTime = optionalString(payload?.scheduled_time);
              const interviewLink = optionalString(payload?.second_interview_link);
              const interviewType = optionalString(payload?.second_interview_type);
              const is2ndOnline = interviewType === 'online';
              const orgName2 = optionalString(payload?.org_name) || secondInterviewDetail?.orgName;

              return (
                <>
                  {/* Interview Details Card */}
                  <div className="border rounded-xl overflow-hidden">
                    {/* Header strip */}
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3 border-b border-indigo-100 dark:border-indigo-800/30">
                      <div className="flex items-center gap-2">
                        {is2ndOnline ? (
                          <Video className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        ) : (
                          <MapPin className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        )}
                        <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                          {is2ndOnline ? t('seek.dashboard.onlineInterview') : t('seek.dashboard.physicalInterview')}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 space-y-3">
                      {/* Organization */}
                      {orgName2 && (
                        <div className="flex items-start gap-3">
                          <Briefcase className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground">{t('seek.dashboard.organizationLabel')}</p>
                            <p className="text-sm font-medium text-foreground">{orgName2}</p>
                          </div>
                        </div>
                      )}

                      {/* Position */}
                      {secondInterviewDetail?.jobTitle && (
                        <div className="flex items-start gap-3">
                          <Briefcase className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground">{t('seek.dashboard.positionLabel')}</p>
                            <p className="text-sm font-medium text-foreground">{secondInterviewDetail.jobTitle}</p>
                          </div>
                        </div>
                      )}

                      {/* Date & Time */}
                      {scheduledDate && (
                        <div className="flex items-start gap-3">
                          <CalendarDays className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground">{t('seek.dashboard.dateTimeLabel')}</p>
                            <p className="text-sm font-medium text-foreground">
                              {scheduledDate}
                              {scheduledTime && <span className="text-muted-foreground"> {t('seek.dashboard.atTime', { time: scheduledTime })}</span>}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Interview Type */}
                      <div className="flex items-start gap-3">
                        {is2ndOnline ? (
                          <Video className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        ) : (
                          <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        )}
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {is2ndOnline ? t('seek.dashboard.meetingLink') : t('seek.dashboard.locationLabel')}
                          </p>
                          {interviewLink ? (
                            <a
                              href={interviewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-primary hover:underline break-all"
                            >
                              {interviewLink}
                            </a>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">{t('seek.dashboard.notProvided')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Message from recruiter */}
                  {secondInterviewNotif?.body && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('seek.dashboard.messageLabel')}</p>
                      <p className="text-sm text-foreground bg-muted/30 rounded-lg p-3 border" dir={detectTextDirection(secondInterviewNotif.body)}>
                        {secondInterviewNotif.body}
                      </p>
                    </div>
                  )}

                  {/* CTA Button */}
                  {interviewLink && (
                    <Button
                      className="w-full bg-seek hover:bg-seek/90 text-seek-foreground gap-1.5"
                      onClick={() => window.open(interviewLink, '_blank')}
                    >
                      {is2ndOnline ? <><Video className="w-4 h-4" /> {t('seek.dashboard.joinMeeting')}</> : <><MapPin className="w-4 h-4" /> {t('seek.dashboard.viewLocation')}</>}
                    </Button>
                  )}

                  {/* Decline Button */}
                  <Button
                    variant="outline"
                    className="w-full gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/5"
                    onClick={() => setF2fDeclineOpen(true)}
                  >
                    <X className="w-4 h-4" /> {t('seek.dashboard.declineF2f')}
                  </Button>

                  {/* Expiry */}
                  {secondInterviewDetail?.expiresAt && (
                    <p className="text-xs text-muted-foreground">
                      <Clock className="w-3 h-3 inline me-1" />
                      {t('seek.dashboard.expiresOn')}: {formatLocalizedDate(secondInterviewDetail.expiresAt, 'PP', language)}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* F2F Decline Confirmation */}
      <AlertDialog open={f2fDeclineOpen} onOpenChange={setF2fDeclineOpen}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">{t('seek.dashboard.declineF2fTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {t('seek.dashboard.declineF2fDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <Select value={f2fDeclineReason} onValueChange={setF2fDeclineReason}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder={t('seek.dashboard.selectReason')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduling_conflict">{t('seek.dashboard.reasonSchedule')}</SelectItem>
                <SelectItem value="not_interested">{t('seek.dashboard.reasonNotInterested')}</SelectItem>
                <SelectItem value="accepted_another">{t('seek.dashboard.reasonAcceptedAnother')}</SelectItem>
                <SelectItem value="location_issue">{t('seek.dashboard.reasonLocation')}</SelectItem>
                <SelectItem value="personal_reasons">{t('seek.dashboard.reasonPersonal')}</SelectItem>
                <SelectItem value="other">{t('seek.dashboard.reasonOther')}</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              value={f2fDeclineNote}
              onChange={(e) => setF2fDeclineNote(e.target.value)}
              placeholder={t('seek.dashboard.declineNotePlaceholder')}
              className="text-sm min-h-[80px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="text-xs bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              disabled={!f2fDeclineReason || f2fDeclining}
              onClick={(e) => { e.preventDefault(); handleF2fDecline(); }}
            >
              {f2fDeclining ? t('common.declining') : t('seek.dashboard.confirmDecline')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </SeekLayout>
  );
}


function InviteStatusBadge({ status, t }: { status: string; t: (key: string) => string }) {
  switch (status) {
    case 'pending':
    case 're-invited':
      return <Badge variant="outline" className="text-[10px] border-seek/40 text-seek"><Briefcase className="w-3 h-3 me-0.5" /> {t('seek.dashboard.newJob')}</Badge>;
    case 'accepted':
      return <Badge variant="default" className="text-[10px] bg-seek"><Check className="w-3 h-3 me-0.5" /> {t('seek.status.accepted')}</Badge>;
    case 'declined':
      return <Badge variant="secondary" className="text-[10px]"><X className="w-3 h-3 me-0.5" /> {t('seek.status.declined')}</Badge>;
    default:
      return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
  }
}
